// src/Pages/Catalog/CatalogCollection.tsx
import { Link } from "react-router-dom";
import React, { useMemo, useState } from "react";
import { Form, Row, Col, Card, Button, Image } from "react-bootstrap";
import Pagination from "Components/Pagination";
import {
  useAddToCartMutation,
  useGetStoreCartQuery,
  useRemoveCartItemMutation,
  useUpdateCartItemQtyMutation,
  type StoreCartItemOut,
} from "../../redux/services/store/storeCartApi";

type CatalogCardItem = {
  id: number;
  slug: string;
  img?: string;
  imgAlt?: string;
  title?: string;
  ratting?: string;
  price?: string;
  deleteproce?: string;
  presentag?: string;
  dic?: number;
  stock?: string;
  bg?: string;
  inStock?: boolean;
  productType?: string;
  cardVariantId?: number | null;
  canDirectAddToCart?: boolean;
  isCustomizable?: boolean;
};

type Props = {
  cxxl?: number;
  cxl?: number;
  clg?: number;
  cmd?: number;
  filterList?: CatalogCardItem[];
  currentPage?: number;
  perPageData?: number;
  totalResults?: number;
  sortValue?: string;
  onSortChange?: (value: string) => void;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  isError?: boolean;
};

const skeletonBaseStyle: React.CSSProperties = {
  width: "100%",
  background:
    "linear-gradient(90deg, rgba(240,240,240,0.95) 25%, rgba(248,248,248,1) 37%, rgba(240,240,240,0.95) 63%)",
  backgroundSize: "400% 100%",
  animation: "catalogCollectionSkeletonShimmer 1.4s ease infinite",
};

const parseQty = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.min(99, Math.round(numeric)));
};

const clampQty = (value: number): number =>
  Math.max(1, Math.min(99, Math.round(value || 1)));

const getMutationErrorText = (error: any): string =>
  error?.data?.detail || error?.error || error?.message || "Unable to update cart";

const CatalogCollection = ({
  cxxl,
  cxl,
  clg,
  cmd,
  filterList,
  currentPage = 1,
  perPageData = 50,
  totalResults = 0,
  sortValue = "all",
  onSortChange,
  onPageChange,
  isLoading = false,
  isError = false,
}: Props) => {
  const pagination: boolean = true;
  const { data: cartData } = useGetStoreCartQuery();
  const [addToCart] = useAddToCartMutation();
  const [updateQty] = useUpdateCartItemQtyMutation();
  const [removeItem] = useRemoveCartItemMutation();
  const [draftQtyByVariantId, setDraftQtyByVariantId] = useState<Record<number, number>>({});
  const [busyVariantIds, setBusyVariantIds] = useState<Record<number, boolean>>({});
  const [actionErrorByVariantId, setActionErrorByVariantId] = useState<Record<number, string>>({});

  const normalizedList = useMemo<CatalogCardItem[]>(
    () => (Array.isArray(filterList) ? [...filterList] : []),
    [filterList]
  );

  const cartItems = useMemo<StoreCartItemOut[]>(
    () => (Array.isArray((cartData as any)?.items) ? ((cartData as any).items as StoreCartItemOut[]) : []),
    [cartData]
  );

  const cartItemByVariantId = useMemo(() => {
    const next = new Map<number, StoreCartItemOut>();
    for (const item of cartItems) {
      const variantId = Number(item?.product?.variant_id);
      if (!Number.isFinite(variantId) || variantId <= 0) continue;
      next.set(variantId, item);
    }
    return next;
  }, [cartItems]);

  const indexOfLast = currentPage * perPageData;
  const indexOfFirst = indexOfLast - perPageData;

  const pageNumbers: number[] = [];
  for (let i = 1; i <= Math.ceil(totalResults / perPageData); i++) {
    pageNumbers.push(i);
  }

  const handleClick = (e: any) => {
    const nextPage = Number(
      e?.currentTarget?.id ?? e?.target?.id ?? currentPage
    );
    if (Number.isFinite(nextPage) && nextPage > 0) {
      onPageChange?.(nextPage);
    }
  };

  const handleprevPage = () =>
    onPageChange?.(Math.max(1, currentPage - 1));

  const handlenextPage = () =>
    onPageChange?.(Math.min(pageNumbers.length || 1, currentPage + 1));

  const selectValue = (value: any) => {
    onSortChange?.(value);
  };

  const showingFrom = totalResults > 0 ? indexOfFirst + 1 : 0;
  const showingTo = Math.min(indexOfLast, totalResults);

  const setVariantBusy = (variantId: number, isBusy: boolean) => {
    setBusyVariantIds((prev) => {
      const next = { ...prev };
      if (isBusy) {
        next[variantId] = true;
      } else {
        delete next[variantId];
      }
      return next;
    });
  };

  const updateDraftQty = (variantId: number, qty: number) => {
    setDraftQtyByVariantId((prev) => ({
      ...prev,
      [variantId]: clampQty(qty),
    }));
  };

  const runVariantAction = async (
    variantId: number,
    action: () => Promise<void>
  ) => {
    setActionErrorByVariantId((prev) => {
      if (!(variantId in prev)) return prev;
      const next = { ...prev };
      delete next[variantId];
      return next;
    });
    setVariantBusy(variantId, true);
    try {
      await action();
    } catch (error) {
      setActionErrorByVariantId((prev) => ({
        ...prev,
        [variantId]: getMutationErrorText(error),
      }));
    } finally {
      setVariantBusy(variantId, false);
    }
  };

  const ProductCardSkeleton = ({ idx }: { idx: number }) => (
    <Col
      key={`catalog-skeleton-${idx}`}
      xxl={cxxl}
      xl={cxl}
      lg={clg}
      md={cmd}
      sm={6}
      xs={6}
      className="mb-4"
    >
      <Card className="catalog-modern-card h-100 border-0 shadow-none overflow-hidden">
        <div className="catalog-modern-image-wrap">
          <div className="catalog-modern-image-box">
            <div
              style={{
                ...skeletonBaseStyle,
                height: "100%",
                minHeight: 220,
              }}
            />
          </div>
        </div>

        <Card.Body className="catalog-modern-body d-flex flex-column">
          <div
            style={{
              ...skeletonBaseStyle,
              height: 14,
              width: "28%",
              borderRadius: 4,
              marginBottom: 12,
            }}
          />
          <div
            style={{
              ...skeletonBaseStyle,
              height: 18,
              width: "92%",
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
          <div
            style={{
              ...skeletonBaseStyle,
              height: 18,
              width: "68%",
              borderRadius: 4,
              marginBottom: 14,
            }}
          />
          <div
            style={{
              ...skeletonBaseStyle,
              height: 18,
              width: "40%",
              borderRadius: 4,
              marginBottom: 16,
            }}
          />
          <div
            style={{
              ...skeletonBaseStyle,
              height: 42,
              width: "100%",
              borderRadius: 999,
              marginTop: "auto",
            }}
          />
        </Card.Body>
      </Card>
    </Col>
  );

  const getProductActionState = (item: CatalogCardItem) => {
    const variantId = Number(item?.cardVariantId);
    const hasCardVariant = Number.isFinite(variantId) && variantId > 0;
    const canDirectAdd =
      Boolean(item?.canDirectAddToCart) && hasCardVariant && item?.inStock !== false;
    const cartItem = hasCardVariant ? cartItemByVariantId.get(variantId) ?? null : null;
    const cartQty = cartItem ? parseQty(cartItem.qty) : 0;
    const draftQty = hasCardVariant
      ? clampQty(draftQtyByVariantId[variantId] ?? 1)
      : 1;

    let helperText: string | null = "Open the product page to review the full product.";
    if (item?.inStock === false) {
      helperText = null;
    } else if (!canDirectAdd) {
      helperText = "Choose size or color on the product page before adding.";
    } else if (item?.isCustomizable) {
      helperText =
        cartQty > 0
          ? `${cartQty} in cart. Open details if you need customization notes.`
          : "Quick add uses the standard product. Open details for customization.";
    } else if (cartQty > 0) {
      helperText = `${cartQty} item${cartQty === 1 ? "" : "s"} already in your cart.`;
    } else {
      helperText = "Use the counter to add this item directly from the catalog.";
    }

    return {
      variantId,
      hasCardVariant,
      canDirectAdd,
      cartItem,
      cartQty,
      draftQty,
      helperText,
      errorText: hasCardVariant ? actionErrorByVariantId[variantId] ?? null : null,
      isBusy: hasCardVariant ? Boolean(busyVariantIds[variantId]) : false,
    };
  };

  const renderCatalogActions = (
    item: CatalogCardItem,
    options: { layout: "grid" | "list"; productUrl: string }
  ) => {
    const { layout, productUrl } = options;
    const state = getProductActionState(item);

    if (layout === "grid") {
      return (
        <div className="catalog-home-bottom-row mt-auto">
          <Link
            to={productUrl}
            className="btn btn-dark catalog-modern-shop-btn catalog-home-shop-btn"
          >
            Shop Now
          </Link>
        </div>
      );
    }

    const alignClass = "catalog-modern-action-stack catalog-modern-action-stack-list";
    const variantId = state.variantId;

    const onDraftDecrease = () => {
      if (!state.hasCardVariant) return;
      updateDraftQty(variantId, state.draftQty - 1);
    };

    const onDraftIncrease = () => {
      if (!state.hasCardVariant) return;
      updateDraftQty(variantId, state.draftQty + 1);
    };

    const onAdd = async () => {
      if (!state.canDirectAdd || !state.hasCardVariant) return;
      await runVariantAction(variantId, async () => {
        await addToCart({
          variant_id: variantId,
          qty: state.draftQty,
        }).unwrap();
        setDraftQtyByVariantId((prev) => {
          const next = { ...prev };
          delete next[variantId];
          return next;
        });
      });
    };

    const onCartDecrease = async () => {
      if (!state.cartItem || !state.hasCardVariant) return;
      await runVariantAction(variantId, async () => {
        if (state.cartQty <= 1) {
          await removeItem({ id: state.cartItem!.id }).unwrap();
          return;
        }
        await updateQty({
          id: state.cartItem!.id,
          qty: clampQty(state.cartQty - 1),
        }).unwrap();
      });
    };

    const onCartIncrease = async () => {
      if (!state.cartItem || !state.hasCardVariant) return;
      await runVariantAction(variantId, async () => {
        await updateQty({
          id: state.cartItem!.id,
          qty: clampQty(state.cartQty + 1),
        }).unwrap();
      });
    };

    return (
      <div className={alignClass}>
        {item?.isCustomizable ? (
          <div className="catalog-card-meta-row">
            <span className="catalog-card-tag-pill">Customizable</span>
          </div>
        ) : null}

        {state.helperText ? (
          <p className="catalog-card-helper mb-0">{state.helperText}</p>
        ) : null}

        {state.canDirectAdd ? (
          state.cartItem ? (
            <div className="catalog-card-action-row">
              <div className="catalog-card-counter" role="group" aria-label="Cart quantity controls">
                <button
                  type="button"
                  className="catalog-card-counter-btn"
                  onClick={onCartDecrease}
                  disabled={state.isBusy}
                  aria-label={`Decrease quantity for ${item?.title || "product"}`}
                >
                  -
                </button>
                <span className="catalog-card-counter-value">{state.cartQty}</span>
                <button
                  type="button"
                  className="catalog-card-counter-btn"
                  onClick={onCartIncrease}
                  disabled={state.isBusy}
                  aria-label={`Increase quantity for ${item?.title || "product"}`}
                >
                  +
                </button>
              </div>

              <span className="catalog-card-inline-note">
                {state.isBusy ? "Updating..." : "In cart"}
              </span>
            </div>
          ) : (
            <div className="catalog-card-action-row">
              <div className="catalog-card-counter" role="group" aria-label="Selected quantity controls">
                <button
                  type="button"
                  className="catalog-card-counter-btn"
                  onClick={onDraftDecrease}
                  disabled={state.isBusy}
                  aria-label={`Decrease quantity for ${item?.title || "product"}`}
                >
                  -
                </button>
                <span className="catalog-card-counter-value">{state.draftQty}</span>
                <button
                  type="button"
                  className="catalog-card-counter-btn"
                  onClick={onDraftIncrease}
                  disabled={state.isBusy}
                  aria-label={`Increase quantity for ${item?.title || "product"}`}
                >
                  +
                </button>
              </div>

              <Button
                className="catalog-modern-shop-btn catalog-modern-add-btn"
                variant="dark"
                onClick={onAdd}
                disabled={state.isBusy}
              >
                {state.isBusy ? "Adding..." : "Add to cart"}
              </Button>
            </div>
          )
        ) : (
          <div className="catalog-card-action-row">
            <Link
              to={productUrl}
              className={`btn ${
                item?.inStock === false ? "btn-outline-dark" : "btn-dark"
              } catalog-modern-shop-btn catalog-modern-add-btn`}
            >
              {item?.inStock === false ? "View details" : "Select options"}
            </Link>
          </div>
        )}

        {state.errorText ? (
          <div className="catalog-card-error" role="alert">
            {state.errorText}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <React.Fragment>
      <style>
        {`
          @keyframes catalogCollectionSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: 0 0; }
          }

          .catalog-results-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 1.25rem;
            flex-wrap: wrap;
          }

          .catalog-results-text {
            color: #6b7280;
            margin-bottom: 0;
            font-weight: 500;
          }

          .catalog-sort-wrap {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .catalog-sort-label {
            margin-bottom: 0 !important;
            color: #111827;
            font-weight: 600;
            white-space: nowrap;
          }

          .catalog-sort-select {
            min-width: 170px;
            min-height: 42px;
            border-radius: 12px;
          }

          .catalog-modern-card {
            border: 1px solid rgba(0, 0, 0, 0.06) !important;
            border-radius: 22px !important;
            overflow: hidden;
            background: #fff;
            transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
          }

          .catalog-modern-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
            border-color: rgba(0, 0, 0, 0.08) !important;
          }

          .catalog-modern-image-wrap {
            position: relative;
            overflow: hidden;
            background: #f8f9fa;
            border-radius: 22px 22px 0 0;
          }

          .catalog-modern-image-box {
            width: 100%;
            aspect-ratio: 3 / 4;
            overflow: hidden;
            background: #f8f9fa;
          }

          .catalog-modern-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
            display: block;
            transition: transform 0.5s ease;
          }

          .catalog-modern-card:hover .catalog-modern-image {
            transform: scale(1.06);
          }

          .catalog-modern-top-actions {
            position: absolute;
            top: 14px;
            right: 14px;
            z-index: 3;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .catalog-modern-icon-btn {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(255,255,255,0.96) !important;
            color: #111 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            border: 0 !important;
            box-shadow: 0 8px 18px rgba(0,0,0,0.12);
            transition: transform 0.25s ease, background 0.25s ease;
            padding: 0 !important;
          }

          .catalog-modern-icon-btn:hover,
          .catalog-modern-icon-btn.active {
            transform: translateY(-2px);
            background: #fff !important;
            color: #111 !important;
          }

          .catalog-modern-discount-badge {
            position: absolute;
            top: 14px;
            left: 14px;
            z-index: 3;
            background: #111;
            color: #fff;
            border-radius: 999px;
            padding: 7px 12px;
            font-size: 12px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.3px;
          }

          .catalog-modern-body {
            padding: 18px;
          }

          .catalog-modern-title {
            color: #111827;
            text-decoration: none;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.35;
            min-height: 43px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-bottom: 0;
          }

          .catalog-modern-title:hover {
            color: #111827;
          }

          .catalog-modern-rating {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            color: #6b7280;
            font-size: 13px;
            font-weight: 600;
          }

          .catalog-modern-price-row {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            margin-top: 10px;
            margin-bottom: 8px;
          }

          .catalog-modern-price-wrap {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .catalog-modern-price {
            font-size: 18px;
            font-weight: 700;
            color: #111;
            line-height: 1;
          }

          .catalog-modern-old-price {
            font-size: 14px;
            font-weight: 500;
            color: #9ca3af;
            text-decoration: line-through;
            line-height: 1;
          }

          .catalog-modern-action-stack {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .catalog-modern-action-stack-list {
            margin-top: 4px;
          }

          .catalog-home-bottom-row {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
          }

          .catalog-home-shop-btn {
            min-height: 44px;
            min-width: 126px;
          }

          .catalog-card-meta-row {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .catalog-card-tag-pill {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 28px;
            padding: 0 12px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.02em;
          }

          .catalog-card-tag-pill {
            background: rgba(17, 24, 39, 0.08);
            color: #111827;
          }

          .catalog-card-helper {
            color: #6b7280;
            font-size: 13px;
            line-height: 1.55;
            min-height: 40px;
          }

          .catalog-card-action-row {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
          }

          .catalog-card-counter {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            padding: 4px;
            border-radius: 999px;
            border: 1px solid rgba(17, 24, 39, 0.1);
            background: #f8fafc;
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
          }

          .catalog-card-counter-btn {
            width: 34px;
            height: 34px;
            border: 0;
            border-radius: 999px;
            background: #fff;
            color: #111827;
            font-size: 18px;
            font-weight: 700;
            line-height: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .catalog-card-counter-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
          }

          .catalog-card-counter-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .catalog-card-counter-value {
            min-width: 36px;
            text-align: center;
            font-size: 15px;
            font-weight: 700;
            color: #111827;
          }

          .catalog-card-inline-note {
            color: #0f766e;
            font-size: 13px;
            font-weight: 700;
          }

          .catalog-card-error {
            color: #b91c1c;
            font-size: 12px;
            font-weight: 600;
          }

          .catalog-modern-shop-btn {
            border-radius: 999px !important;
            padding: 10px 16px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            line-height: 1 !important;
            border: 0 !important;
            text-decoration: none !important;
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
          }

          .catalog-modern-add-btn {
            min-height: 44px;
            min-width: 150px;
          }

          .catalog-list-card {
            border: 1px solid rgba(0, 0, 0, 0.06) !important;
            border-radius: 22px !important;
            overflow: hidden;
            background: #fff;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
            transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
          }

          .catalog-list-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
            border-color: rgba(0, 0, 0, 0.08) !important;
          }

          .catalog-list-image-box {
            width: 100%;
            aspect-ratio: 3 / 4;
            overflow: hidden;
            border-radius: 18px;
            background: #f8f9fa;
          }

          .catalog-list-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
            display: block;
            transition: transform 0.5s ease;
          }

          .catalog-list-card:hover .catalog-list-image {
            transform: scale(1.05);
          }

          .catalog-list-title {
            color: #111827;
            text-decoration: none;
            font-size: 18px;
            font-weight: 600;
            line-height: 1.35;
          }

          .catalog-list-title:hover {
            color: #111827;
          }

          .catalog-list-price {
            font-size: 18px;
            font-weight: 700;
            color: #111;
            line-height: 1;
          }

          .catalog-list-old-price {
            font-size: 14px;
            font-weight: 500;
            color: #9ca3af;
            text-decoration: line-through;
            line-height: 1;
          }

          @media (max-width: 767.98px) {
            .catalog-results-toolbar {
              align-items: stretch;
              gap: 12px;
              margin-bottom: 1rem;
            }

            .catalog-results-text {
              width: 100%;
              font-size: 13px;
            }

            .catalog-sort-wrap {
              width: 100%;
              align-items: stretch;
              flex-direction: column;
              gap: 8px;
            }

            .catalog-sort-select {
              width: 100%;
              min-width: 100%;
            }
          }

          @media (max-width: 575.98px) {
            #product-grid > [class*="col-"] {
              margin-bottom: 1rem !important;
            }

            .catalog-modern-body {
              padding: 14px;
            }

            .catalog-modern-title {
              font-size: 15px;
              min-height: 40px;
            }

            .catalog-modern-price,
            .catalog-list-price {
              font-size: 17px;
            }

            .catalog-home-bottom-row {
              flex-direction: column;
              align-items: stretch;
            }

            .catalog-home-shop-btn {
              width: 100%;
            }

            .catalog-card-action-row {
              flex-direction: column;
              align-items: stretch;
            }

            .catalog-modern-shop-btn {
              width: 100%;
            }

            .catalog-modern-add-btn {
              width: 100%;
              min-width: 100%;
            }

            .catalog-card-counter {
              width: 100%;
              justify-content: space-between;
            }

            .catalog-modern-icon-btn {
              width: 36px;
              height: 36px;
            }

            .catalog-modern-discount-badge {
              top: 12px;
              left: 12px;
              padding: 6px 10px;
              font-size: 11px;
            }

            .catalog-modern-top-actions {
              top: 12px;
              right: 12px;
            }
          }
        `}
      </style>

      <div className="flex-grow-1">
        <div className="catalog-results-toolbar">
          <p className="catalog-results-text">
            {isLoading
              ? "Loading products..."
              : `Showing ${showingFrom}-${showingTo} of ${totalResults} results`}
          </p>

          <div className="catalog-sort-wrap">
            <Form.Label htmlFor="sort-elem" className="catalog-sort-label">
              Sort By:
            </Form.Label>
            <Form.Select
              className="catalog-sort-select"
              id="sort-elem"
              value={sortValue}
              onChange={(e) => selectValue(e.target.value)}
              disabled={isLoading}
            >
              <option value="all">All</option>
              <option value="lowtohigh">Low to High</option>
              <option value="hightolow">High to Low</option>
            </Form.Select>
          </div>
        </div>

        {isLoading ? (
          <Row id="product-grid">
            {Array.from({ length: 8 }).map((_, idx) => (
              <ProductCardSkeleton key={idx} idx={idx} />
            ))}
          </Row>
        ) : normalizedList && normalizedList.length > 0 ? (
          <Row id="product-grid">
            {normalizedList.map((item: CatalogCardItem, idx: number) => {
              const productUrl = `/product/${item.slug}`;
              const badgeText =
                item?.presentag || (item?.dic ? `${item.dic}% OFF` : null);

              return !cxl ? (
                <Col
                  key={item.id ?? idx}
                  xxl={cxxl}
                  xl={cxl}
                  lg={clg}
                  md={cmd}
                  sm={6}
                  xs={6}
                  className="mb-4"
                >
                  <Card className="catalog-modern-card h-100 border-0 shadow-none">
                    <div className="catalog-modern-image-wrap">
                      {badgeText ? (
                        <span className="catalog-modern-discount-badge">
                          {badgeText}
                        </span>
                      ) : null}

                      <div className="catalog-modern-top-actions">
                        <Link
                          to={productUrl}
                          className="catalog-modern-icon-btn"
                          aria-label={`View ${item?.title || "product"}`}
                        >
                          <i className="bi bi-eye"></i>
                        </Link>
                        <Link
                          to={productUrl}
                          className="catalog-modern-icon-btn"
                          aria-label={`Shop ${item?.title || "product"}`}
                        >
                          <i className="bi bi-bag"></i>
                        </Link>
                      </div>

                      <Link to={productUrl} className="d-block w-100 h-100 text-decoration-none">
                        <div className="catalog-modern-image-box">
                          <Image
                            src={item.img}
                            alt={item?.imgAlt || item?.title || ""}
                            fluid
                            className="catalog-modern-image"
                          />
                        </div>
                      </Link>
                    </div>

                    <Card.Body className="catalog-modern-body d-flex flex-column">
                      <Link to={productUrl} className="catalog-modern-title text-decoration-none">
                        {item?.title}
                      </Link>

                      <div className="catalog-modern-price-row">
                        <div className="catalog-modern-price-wrap">
                          <span className="catalog-modern-price">{item?.price}</span>
                          {item?.deleteproce ? (
                            <span className="catalog-modern-old-price">
                              {item?.deleteproce}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {renderCatalogActions(item, { layout: "grid", productUrl })}
                    </Card.Body>
                  </Card>
                </Col>
              ) : (
                <Col lg={12} key={item.id ?? idx} className="mb-4">
                  <Card className="catalog-list-card border-0 shadow-none">
                    <Card.Body className="p-3 p-lg-4">
                      <Row className="align-items-center g-4">
                        <Col md={4}>
                          <Link to={productUrl} className="d-block">
                            <div className="catalog-list-image-box">
                              <Image
                                src={item.img}
                                alt={item?.imgAlt || item?.title || ""}
                                fluid
                                className="catalog-list-image"
                              />
                            </div>
                          </Link>
                        </Col>

                        <Col md={8}>
                          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-2">
                            <div className="catalog-modern-rating">
                              <span>{item?.ratting || "4.9"}</span>
                              <i className="ri-star-fill text-warning"></i>
                            </div>

                            {badgeText ? (
                              <span className="catalog-modern-discount-badge position-static">
                                {badgeText}
                              </span>
                            ) : null}
                          </div>

                          <Link to={productUrl} className="catalog-list-title">
                            {item?.title}
                          </Link>

                          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="catalog-list-price">{item?.price}</span>
                              {item?.deleteproce ? (
                                <span className="catalog-list-old-price">
                                  {item?.deleteproce}
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {renderCatalogActions(item, { layout: "list", productUrl })}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Row id="search-result-elem">
            <Col lg={12}>
              <div className="text-center py-5">
                <div className="avatar-lg mx-auto mb-4">
                  <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                    <i className="bi bi-search"></i>
                  </div>
                </div>
                <h5>{isError ? "Unable to load products" : "No matching records found"}</h5>
              </div>
            </Col>
          </Row>
        )}

        {!isLoading && totalResults > 0 ? (
          <Pagination
            pagination={pagination}
            pageNumbers={pageNumbers}
            currentpages={normalizedList}
            currentPage={currentPage}
            handleprevPage={handleprevPage}
            handleClick={handleClick}
            handlenextPage={handlenextPage}
          />
        ) : null}
      </div>
    </React.Fragment>
  );
};

export default CatalogCollection;
